/**
 * Tipos do dominio "devices" no frontend.
 *
 * Modelados a partir dos DTOs reais que a API do dashboard-ghm devolve:
 *
 *   GET /api/devices/discovered   -> Device[]
 *   GET /api/devices/:id          -> Device
 *   GET /api/devices/:id/dashboard
 *     -> {
 *          device:       Device,
 *          currentMeta:  CurrentMeta | null,
 *          latestSync:   LatestSync  | null,
 *          latestPorts:  LatestPort[],
 *        }
 *
 * Importante:
 *  - currentMeta.metaJson eh STRING (JSON serializado). Quem precisar
 *    dos campos internos chama o parser em adapters.ts.
 *  - O identificador estavel da rota e do endpoint e Device.id
 *    (mqttId so e exibido).
 */

export type IsoDateString = string;

export type DeviceStatus =
  | "online"
  | "offline"
  | "claimed"
  | "unclaimed"
  | "unknown"
  | (string & {});

export interface Device {
  id: string;
  /** Identidade de REDE. Imutavel — casada com a ACL do broker. */
  mqttId: string;
  /**
   * Identidade OPERACIONAL ("Extrusora 2"). `null`/ausente = ninguem nomeou.
   * Nao leia direto para exibir: use `nomeDeExibicao`, que trata o vazio.
   */
  nomeFantasia?: string | null;
  serialNumber?: string | null;
  mac?: string | null;
  status?: DeviceStatus | null;
  currentWorkspaceId?: string | null;
  firstSeenAt?: IsoDateString | null;
  lastSeenAt?: IsoDateString | null;
}

export interface CurrentMeta {
  deviceId: string;
  configHashHex?: string | null;
  firmwareVersion?: string | null;
  configSchema?: string | number | null;
  /** JSON serializado como string. Use parseMetaJson() em adapters.ts. */
  metaJson: string;
  receivedAt?: IsoDateString | null;
}

export interface LatestSync {
  deviceId: string;
  mqttId?: string | null;
  deviceUptimeSec?: number | null;
  uptimeMs?: number | null;
  ip?: string | null;
  rssi?: number | null;
  firmwareVersion?: string | null;
  measuredAt?: IsoDateString | null;
  configHashHex?: string | null;
}

export interface LatestPort {
  deviceId: string;
  mqttId?: string | null;
  portIndex: number;
  mode?: string | number | null;
  v?: number | string | null;
  acc?: number | string | null;
  s?: number | string | boolean | null;
  measuredAt?: IsoDateString | null;
  configHashHex?: string | null;
  source?: "telemetry" | "sync" | "legacy" | null;
  receivedAt?: IsoDateString | null;
}

/**
 * Janelas oficiais de historico, na ordem do seletor. 6h/12h existem porque
 * o operador quer enxergar o turno em curso com resolucao de minuto sem
 * arrastar 24 h de pontos; abaixo de 24 h a API usa buckets de 1 minuto.
 */
export const PRODUCTION_HISTORY_WINDOWS = ["6h", "12h", "24h", "48h", "7d", "30d"] as const;
export type ProductionHistoryWindow = (typeof PRODUCTION_HISTORY_WINDOWS)[number];

export function isProductionHistoryWindow(value: unknown): value is ProductionHistoryWindow {
  return typeof value === "string" && (PRODUCTION_HISTORY_WINDOWS as readonly string[]).includes(value);
}

export interface ProductionHistoryPoint {
  bucket: IsoDateString;
  configHashHex: string;
  accFirst: number | null;
  accLast: number | null;
  delta: number;
  vLast: number | null;
  vAvg: number | null;
  vMin: number | null;
  vMax: number | null;
  sampleCount: number;
  activeSeconds: number;
  quality: number;
}

export interface ProductionHistory {
  deviceId: string;
  portIndex: number;
  window: ProductionHistoryWindow;
  resolution: "1m" | "1h" | "1d";
  from: IsoDateString;
  to: IsoDateString;
  total: number;
  configHashHex?: string;
  points: ProductionHistoryPoint[];
}

export type PortAvailabilityStateKind = "running" | "stopped" | "gap";

export interface PortAvailabilityEpisode {
  state: PortAvailabilityStateKind;
  from: IsoDateString;
  to: IsoDateString;
  seconds: number;
}

export interface PortAvailability {
  configHashHex?: string;
  deviceId: string;
  portIndex: number;
  window: ProductionHistoryWindow;
  resolution: "1m" | "1h" | "1d";
  from: IsoDateString;
  to: IsoDateString;
  windowSeconds: number;
  observedSeconds: number;
  runningSeconds: number;
  stoppedSeconds: number;
  /** So o que o contador nao explica: buraco > 2 min, borda da janela, placa fora do ar. */
  gapSeconds: number;
  /**
   * Segundos sem telemetria classificados pela evidencia do contador (`acc`),
   * ja somados em `observedSeconds`. O card nao os destaca: com teto de
   * 2 min por buraco, o rotulo confundiria mais do que informaria.
   */
  bridgedSeconds: number;
  utilizationPercent: number | null;
  episodes: PortAvailabilityEpisode[];
}

export interface LatestDigitalInput {
  deviceId: string;
  mqttId?: string | null;
  portIndex: number;
  address: string;
  gpio: number;
  level: number;
  active: number;
  polarity?: string | null;
  measuredAt?: IsoDateString | null;
  configHashHex?: string | null;
}

export interface DeviceDashboardResponse {
  device: Device;
  currentMeta: CurrentMeta | null;
  latestSync: LatestSync | null;
  latestPorts: LatestPort[];
  latestDigitalInputs?: LatestDigitalInput[];
  syncHistory?: TelemetryHealthPoint[];
}

export interface TelemetryHealthPoint {
  measuredAt: IsoDateString;
}

export interface ProductionCycleRecord {
  id: string;
  sequenceNo: number;
  reason: "target" | "manual" | "interrupted";
  reasonDetail: string | null;
  configHashHex: string;
  startedAt: IsoDateString;
  closedAt: IsoDateString;
  startAcc: number;
  endAcc: number;
  quantity: number;
  target: number | null;
}

export interface ProductionTracker {
  id: string;
  workspaceId: string;
  deviceId: string;
  portIndex: number;
  configHashHex: string;
  unit: string | null;
  target: number | null;
  currentAmount: number;
  progressPercent: number | null;
  remainingAmount: number | null;
  completedCycles: number;
  automaticCycles: number;
  manualResets: number;
  interruptedCycles: number;
  startedAt: IsoDateString;
  lastMeasuredAt: IsoDateString;
  recentCycles: ProductionCycleRecord[];
}

/* ---------------- metaJson (apos parse) ---------------- */

/**
 * Tipagem aberta para o blob de meta apos parse. So tipamos a chave
 * que a UI realmente consome (functions[]). O resto fica acessivel
 * via index-signature, sem inventar contrato.
 */
export interface MetaFunctionEntry {
  /** Indice da porta fisica associada. */
  p?: number;
  /** Modo / mode. */
  m?: string | number;
  /** Identificador logico da funcao. */
  id?: string | number;
  /** Rotulo amigavel. */
  label?: string;
  /** Fonte primaria de leitura. */
  source?: string;
  /** Fonte secundaria (alguns modos usam). */
  sourceB?: string;
  /**
   * Definicoes publicadas por sinal (`s`, `acc`, `v`) ou lista legada.
   *
   * `kind`, `decimals`, `min` e `max` sao opcionais por design: o firmware so
   * emite `kind` quando DIVERGE do default deduzivel de `(modo, sinal)`, e a
   * faixa so existe no analogico. Ver `resolveSignalKind` em
   * `@ghm/mqtt-contract`.
   */
  values?:
    | Array<string | number>
    | Partial<
        Record<
          "s" | "acc" | "v",
          {
            label?: string;
            unit?: string;
            kind?: string;
            decimals?: number;
            min?: number;
            max?: number;
          }
        >
      >;
  [k: string]: unknown;
}

export interface ParsedMeta {
  functions?: MetaFunctionEntry[];
  [k: string]: unknown;
}
